// frontend/src/components/team/WorkflowDialog.tsx
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { skillsPluginsApi } from '../../services/api';
import type { Workflow, WorkflowCreate } from '../../services/api';

const INTEGRATIONS = [
  { id: 'jira', label: 'JIRA' },
  { id: 'github', label: 'GitHub' },
  { id: 'gitlab', label: 'GitLab' },
  { id: 'gchat', label: 'Google Chat' },
  { id: 'confluence', label: 'Confluence' },
];

const EVENT_TYPES = [
  { value: 'sprint_end', label: 'Sprint End' },
  { value: 'ticket_label_added', label: 'Ticket Label Added' },
  { value: 'ticket_status_changed', label: 'Ticket Status Changed' },
  { value: 'pr_merged', label: 'PR Merged' },
  { value: 'pr_review_requested', label: 'PR Review Requested' },
  { value: 'build_failed', label: 'Build Failed' },
];

const TEMPLATES = [
  {
    id: 'daily_brief',
    label: 'Daily Brief',
    prompt:
      'Generate a daily brief for the team. Include: 1) All open P1/P2 JIRA tickets, 2) Any tickets marked as blockers, 3) Open incidents or critical bugs, 4) PRs waiting for review from team members. Format as a concise bullet-point summary.',
    integrations: ['jira', 'github'],
    cron: '0 9 * * 1-5',
  },
  {
    id: 'sprint_summary',
    label: 'Sprint Summary',
    prompt:
      'Generate a sprint summary. Include: 1) Tickets completed this sprint, 2) Tickets still in progress, 3) Tickets not started, 4) Sprint velocity vs previous sprint. Format as a structured report.',
    integrations: ['jira'],
    cron: '0 9 * * 5',
  },
  {
    id: 'weekly_scan',
    label: 'Weekly Project Scan',
    prompt:
      'Scan all open cloud/infra projects. For each open project: 1) Current status, 2) Blockers, 3) Next milestone. Send a concise weekly update.',
    integrations: ['jira', 'github'],
    cron: '0 9 * * 1',
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: WorkflowCreate) => void;
  initial?: Workflow | null;
  isSaving?: boolean;
  teamId: string;
}

const defaultForm = (): WorkflowCreate => ({
  name: '',
  description: '',
  trigger_type: 'manual',
  cron_expression: '',
  event_type: '',
  goal_prompt: '',
  enabled_integrations: [],
  selected_skills: [],
  output_destinations: [{ type: 'log' }],
  is_active: true,
});

// Must match backend WORKFLOW_TIMEZONE setting in settings.py
const WORKFLOW_SERVER_TZ = 'Europe/Berlin';

const getServerTZOffsetMinutes = (): number => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: WORKFLOW_SERVER_TZ,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(now);
  const tzHour = parseInt(parts.find((p) => p.type === 'hour')!.value, 10);
  const tzMin = parseInt(parts.find((p) => p.type === 'minute')!.value, 10);
  let offset = tzHour * 60 + tzMin - (now.getUTCHours() * 60 + now.getUTCMinutes());
  if (offset > 720) offset -= 1440;
  if (offset < -720) offset += 1440;
  return offset;
};

const parseCronPreview = (expr: string): string | null => {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minuteStr, hourStr] = parts;
  const minute = parseInt(minuteStr, 10);
  const hour = parseInt(hourStr, 10);
  if (isNaN(minute) || isNaN(hour)) return null;

  // Convert server TZ → UTC using current date offset (DST-aware)
  const serverOffset = getServerTZOffsetMinutes();
  const utcMinutes = ((hour * 60 + minute - serverOffset) % 1440 + 1440) % 1440;
  const utcH = Math.floor(utcMinutes / 60);
  const utcM = utcMinutes % 60;

  // Display local equivalent using today's Date (handles summer/winter DST correctly)
  const d = new Date();
  d.setUTCHours(utcH, utcM, 0, 0);
  const localStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const serverStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${WORKFLOW_SERVER_TZ}`;
  return `Fires at ${serverStr} (${localStr} ${tz})`;
};

const WorkflowDialog: React.FC<Props> = ({ open, onClose, onSave, initial, isSaving, teamId }) => {
  const [form, setForm] = useState<WorkflowCreate>(defaultForm());

  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ['team-skills-plugins', teamId],
    queryFn: () => skillsPluginsApi.getTeamCatalog(teamId).then((r) => r.data),
    enabled: open && !!teamId,
  });

  const enabledSkills = (catalog ?? []).filter((e) => e.enabled);

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name,
              description: initial.description ?? '',
              trigger_type: initial.trigger_type,
              cron_expression: initial.cron_expression ?? '',
              event_type: initial.event_type ?? '',
              goal_prompt: initial.goal_prompt,
              template_id: initial.template_id ?? undefined,
              enabled_integrations: initial.enabled_integrations,
              selected_skills: initial.selected_skills ?? [],
              output_destinations: initial.output_destinations,
              is_active: initial.is_active,
            }
          : defaultForm()
      );
    }
  }, [open, initial]);

  const applyTemplate = (templateId: string) => {
    const t = TEMPLATES.find((t) => t.id === templateId);
    if (!t) return;
    setForm((f) => ({
      ...f,
      name: t.label,
      goal_prompt: t.prompt,
      enabled_integrations: t.integrations,
      trigger_type: 'cron',
      cron_expression: t.cron,
      template_id: t.id,
    }));
  };

  const toggleIntegration = (id: string) => {
    setForm((f) => ({
      ...f,
      enabled_integrations: f.enabled_integrations.includes(id)
        ? f.enabled_integrations.filter((i) => i !== id)
        : [...f.enabled_integrations, id],
    }));
  };

  const toggleSkill = (id: string) => {
    setForm((f) => ({
      ...f,
      selected_skills: (f.selected_skills ?? []).includes(id)
        ? (f.selected_skills ?? []).filter((s) => s !== id)
        : [...(f.selected_skills ?? []), id],
    }));
  };

  const handleSave = () => {
    const payload: WorkflowCreate = {
      ...form,
      cron_expression: form.trigger_type === 'cron' ? form.cron_expression : undefined,
      event_type: form.trigger_type === 'event' ? form.event_type : undefined,
    };
    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Edit Workflow' : 'New Workflow'}</DialogTitle>
      <DialogContent dividers>
        {/* Template shortcuts */}
        {!initial && (
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Start from template:
            </Typography>
            <Box display="flex" gap={0.75} flexWrap="wrap">
              {TEMPLATES.map((t) => (
                <Chip
                  key={t.id}
                  label={t.label}
                  size="small"
                  clickable
                  onClick={() => applyTemplate(t.id)}
                  variant={form.template_id === t.id ? 'filled' : 'outlined'}
                  color={form.template_id === t.id ? 'primary' : 'default'}
                />
              ))}
            </Box>
          </Box>
        )}

        <TextField
          label="Name"
          fullWidth
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          required
          sx={{ mb: 2 }}
        />
        <TextField
          label="Description (optional)"
          fullWidth
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" mb={1}>
          Trigger
        </Typography>

        <Select
          fullWidth
          value={form.trigger_type}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              trigger_type: e.target.value as WorkflowCreate['trigger_type'],
            }))
          }
          sx={{ mb: 1.5 }}
          size="small"
        >
          <MenuItem value="manual">Manual (Run Now only)</MenuItem>
          <MenuItem value="cron">Scheduled (Cron)</MenuItem>
          <MenuItem value="event">Event-driven</MenuItem>
        </Select>

        {form.trigger_type === 'cron' && (
          <>
            <TextField
              label="Cron expression (e.g. 0 7 * * 1-5)"
              fullWidth
              value={form.cron_expression}
              onChange={(e) => setForm((f) => ({ ...f, cron_expression: e.target.value }))}
              helperText="Europe/Berlin time · 5-part cron: minute hour day month weekday"
              size="small"
              sx={{ mb: 0.5 }}
            />
            {(() => {
              const preview = parseCronPreview(form.cron_expression ?? '');
              return preview ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, pl: 0.5 }}>
                  → {preview}
                </Typography>
              ) : (
                <Box sx={{ mb: 1.5 }} />
              );
            })()}
          </>
        )}

        {form.trigger_type === 'event' && (
          <Select
            fullWidth
            value={form.event_type ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}
            sx={{ mb: 1.5 }}
            size="small"
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select event type
            </MenuItem>
            {EVENT_TYPES.map((et) => (
              <MenuItem key={et.value} value={et.value}>
                {et.label}
              </MenuItem>
            ))}
          </Select>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" mb={1}>
          Integrations
        </Typography>
        <FormGroup row>
          {INTEGRATIONS.map((intg) => (
            <FormControlLabel
              key={intg.id}
              control={
                <Checkbox
                  size="small"
                  checked={form.enabled_integrations.includes(intg.id)}
                  onChange={() => toggleIntegration(intg.id)}
                />
              }
              label={intg.label}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" mb={1}>
          Skills &amp; Plugins
        </Typography>
        {catalogLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : enabledSkills.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No skills or plugins enabled for this team.
          </Typography>
        ) : (
          <FormGroup row>
            {enabledSkills.map((skill) => (
              <FormControlLabel
                key={skill.id}
                control={
                  <Checkbox
                    size="small"
                    checked={(form.selected_skills ?? []).includes(skill.id)}
                    onChange={() => toggleSkill(skill.id)}
                  />
                }
                label={skill.name}
              />
            ))}
          </FormGroup>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" mb={1}>
          Goal / Prompt
        </Typography>
        <TextField
          label="What should the agent do?"
          fullWidth
          multiline
          minRows={4}
          value={form.goal_prompt}
          onChange={(e) => setForm((f) => ({ ...f, goal_prompt: e.target.value }))}
          required
          sx={{ mb: 1.5 }}
        />

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" mb={1}>
          Output Destinations
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Where should the workflow send its results?
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={form.output_destinations.some((d) => d.type === 'log')}
                onChange={(e) => {
                  setForm((f) => ({
                    ...f,
                    output_destinations: e.target.checked
                      ? [...f.output_destinations.filter((d) => d.type !== 'log'), { type: 'log' }]
                      : f.output_destinations.filter((d) => d.type !== 'log'),
                  }));
                }}
              />
            }
            label="Activity Log"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={form.output_destinations.some((d) => d.type === 'gchat')}
                onChange={(e) => {
                  setForm((f) => ({
                    ...f,
                    output_destinations: e.target.checked
                      ? [...f.output_destinations.filter((d) => d.type !== 'gchat'), { type: 'gchat', space_id: '' }]
                      : f.output_destinations.filter((d) => d.type !== 'gchat'),
                  }));
                }}
              />
            }
            label="Google Chat Space"
          />
          {form.output_destinations.some((d) => d.type === 'gchat') && (
            <TextField
              label="Space ID"
              size="small"
              value={form.output_destinations.find((d) => d.type === 'gchat')?.space_id ?? ''}
              onChange={(e) => {
                setForm((f) => ({
                  ...f,
                  output_destinations: f.output_destinations.map((d) =>
                    d.type === 'gchat' ? { ...d, space_id: e.target.value } : d
                  ),
                }));
              }}
              placeholder="spaces/XXXXXXXXX"
              sx={{ ml: 4, mb: 1, maxWidth: 320 }}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={form.output_destinations.some((d) => d.type === 'dm')}
                onChange={(e) => {
                  setForm((f) => ({
                    ...f,
                    output_destinations: e.target.checked
                      ? [...f.output_destinations.filter((d) => d.type !== 'dm'), { type: 'dm', emails: '' }]
                      : f.output_destinations.filter((d) => d.type !== 'dm'),
                  }));
                }}
              />
            }
            label="Direct Message"
          />
          {form.output_destinations.some((d) => d.type === 'dm') && (
            <TextField
              label="Recipient emails"
              size="small"
              value={form.output_destinations.find((d) => d.type === 'dm')?.emails ?? ''}
              onChange={(e) => {
                setForm((f) => ({
                  ...f,
                  output_destinations: f.output_destinations.map((d) =>
                    d.type === 'dm' ? { ...d, emails: e.target.value } : d
                  ),
                }));
              }}
              helperText="Comma-separated email addresses of DM contacts"
              placeholder="alice@example.com, bob@example.com"
              sx={{ ml: 4, mb: 1, maxWidth: 400 }}
            />
          )}
        </FormGroup>

        <Divider sx={{ my: 1.5 }} />
        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!form.name || !form.goal_prompt || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowDialog;
